import { cloneElement, isValidElement, type ReactElement, type ReactNode, useRef } from 'react';
import cn from 'classnames';
import { HStack, Link, Text, VStack } from '@direct-frontend/components';
import AliceLogo from './assets/AliceLogo.react.svg';
import KitLogo from './assets/KitLogo.react.svg';
import RatingStarIcon from './assets/RatingStarIcon.react.svg';
import WithIcon from './assets/WithIcon.react.svg';
import YandexLogo from './assets/YandexLogo.react.svg';
import { pluralize } from '../../lib/pluralize';
import { getRequestContext } from '../../lib/requestContext';
import { ScrollContext } from '../Sticky';
import styles from './Layout.module.css';

const Logo = (props: { expressCheckoutSource: string }) =>
    props.expressCheckoutSource === 'alice_chat' ? (
        <AliceLogo width="30" height="30" role="presentation" />
    ) : (
        <KitLogo className={styles.kitLogo} role="presentation" />
    );

interface LayoutProps {
    logoSlot?: ReactElement<HTMLElement>;
    sidebarSlot?: ReactElement<HTMLElement>;
    accountSlot?: ReactElement<HTMLElement>;
    footerSlot?: ReactElement<HTMLElement>;
    companyShopUrl?: string;
    children: ReactNode;
}

export const Layout = (props: LayoutProps) => {
    const { sidebarSlot = null, accountSlot = null, footerSlot = null, logoSlot, companyShopUrl, children } = props;
    const ref = useRef<HTMLDivElement>(null);
    const { merchanthRating, isExpressCheckout, expressCheckoutSource } = getRequestContext();

    return (
        <div className={styles.root} ref={ref}>
            <ScrollContext.Provider value={{ scrollRef: ref, offsetTop: 0 }}>
                <header className={styles.header}>
                    <HStack alignItems="center" justifyContent="space-between" className={styles.headerInner}>
                        <VStack gap="2">
                            <HStack alignItems="center" className={styles.logoWrapper}>
                                <Link href={companyShopUrl}>
                                    {logoSlot || <YandexLogo height="36" role="presentation" />}
                                </Link>
                                <WithIcon height="12" role="presentation" />
                                <Logo expressCheckoutSource={expressCheckoutSource} />
                            </HStack>
                            {logoSlot && isExpressCheckout && merchanthRating && merchanthRating.reviewCount > 0 && (
                                <HStack gap="1">
                                    <RatingStarIcon />
                                    <Text bold size="s">
                                        {merchanthRating.rating}
                                    </Text>
                                    <Text size="s">&nbsp;•&nbsp;</Text>
                                    <Text role="supplementary" size="s">
                                        {merchanthRating.reviewCount}{' '}
                                        {pluralize(merchanthRating.reviewCount, 'отзыв', 'отзыва', 'отзывов')} на
                                        магазин
                                    </Text>
                                </HStack>
                            )}
                        </VStack>
                        {accountSlot}
                    </HStack>
                </header>
                <div className={styles.inner}>
                    <main className={styles.content}>{children}</main>
                    {isValidElement(sidebarSlot) ? (
                        <aside className={styles.sidebar}>
                            <div className={styles.sidebarInner}>
                                {cloneElement(sidebarSlot, {
                                    className: cn(sidebarSlot.props.className, styles.sidebarContent),
                                })}
                            </div>
                        </aside>
                    ) : null}
                </div>

                {isValidElement(footerSlot) ? <footer>{footerSlot}</footer> : null}
            </ScrollContext.Provider>
        </div>
    );
};
